import React from "react";

/**
 * ActionButton — issue #750 § 5.5. The one gold primary action per screen
 * (rule R-003 — verified: every one of the 22 approved screens has exactly
 * one `background: #D4A017`). The `boxShadow` style below is explicitly
 * `"none"`, not left to default, because the shadcn `Button` this replaces
 * (`components/ui/button.jsx:8`) ships a shadow.
 *
 * SPANS ITS ROW BY DEFAULT (issue #793). Every drawn instance of the gold
 * action fills the column it sits in — the five call sites this replaced
 * (`OnboardingNew.jsx` ×2, `CoachSetUp.jsx`, `CoachPortalEmptyState.jsx`,
 * `SignUpNew.jsx`) used to each diagnose their own container and pick the
 * right incantation (`flexGrow: 1` beside a sibling, `width: "100%"`
 * alone, or — `SignUpNew.jsx`, measured, not assumed — nothing at all,
 * rescued by its own flex-column parent's default stretch) for a property
 * the drawings specify identically everywhere.
 *
 * `display: "flex"` (not `"inline-flex"`) is what does the actual work,
 * for two different reasons depending on the parent — not one mechanism
 * covering both. Where the parent is a plain block
 * (`CoachSetUp`/`CoachPortalEmptyState`'s `space-y-*` divs, not a flex
 * container), this element is never blockified as a flex item — it isn't
 * one, because blockification only applies to a flex container's OWN
 * items. `display: "flex"` is itself a block-level value (unlike
 * `inline-flex`), so the element becomes a block box on its own account,
 * and a block box's `width: auto` already fills its containing block —
 * the `width: "100%"` below is belt-and-suspenders for that case, not the
 * only thing doing the work. Where the parent IS a flex container
 * (`SignUpNew.jsx`'s flex-column), this element IS a flex item, and a
 * flex item is blockified for layout purposes regardless of its own
 * specified display — but blockification alone doesn't explain the
 * width there; the parent's `align-items: stretch` default is what
 * widens this item's cross axis to fill the column.
 *
 * `OnboardingNew.jsx`'s two sites are the exception, and the call sites
 * below that still need to know their container: they share their row
 * with `SecondaryAction` ("Skip for now") and keep a local
 * `flexGrow: 1` + `width: "auto"` override rather than take the kit
 * default. Measured, not assumed: dropping that override and relying on
 * the kit's bare `width: "100%"` does NOT converge on the same pixel
 * width (232.453px vs 200.375px,
 * `frontend/e2e/journeys/30-action-button-width.journey.spec.js`) —
 * `width: "100%"` becomes this item's flex-basis, which overcommits the
 * row, and `SecondaryAction`'s
 * boxed variant carries NO `white-space: nowrap` of its own (only a call
 * site can add one — `CoachSetUp.jsx:191` does, `OnboardingNew.jsx`'s Skip
 * does not), so the flex-shrink pass that then has to fit both items
 * shrinks Skip's TEXT onto a second line instead of just its box. The
 * drawing agrees this caller needs to know its situation:
 * `SetUp.dc.html:117-119` (`design-baseline-round-4`) draws this row as
 * `display: flex; gap: 10px` with Continue at `flex-grow: 1`, not
 * `width: 100%` — the drawing's own protection against the wrap. So:
 * three of the five render sites need no container knowledge; the other
 * two — both in `OnboardingNew.jsx` — keep `flexGrow: 1` + `width: "auto"`,
 * and the journey verifies both (row-sharing case included, not just the
 * three standalone sites).
 *
 * LABEL/ICON COLOUR (round 5 conformance, D27, issue #864). Every drawn
 * gold action pairs `--nw-gold` with the near-black `--nw-ink`
 * (`#0F172A`), not white — `DirectionA1.dc.html:72-74`,
 * `FirstGround.dc.html:86-88`, `Saved.dc.html:124` among others. This used
 * to default to `#FFFFFF` here, which every already-drawn call site then
 * had to override one property at a time (`style={{ color: "var(--nw-ink)"
 * }}` on the button, an explicit `color` prop on the icon, because the icon
 * wrapper below hardcoded white too) — SignUp/SetUp/CoachSetUp/
 * CoachPortalEmpty's gold actions were never given that override and
 * shipped white-on-gold. The default is the drawn colour now; the
 * per-call overrides are gone (git blame this file's history for the
 * removed workarounds — `LogMatchGroundStep.jsx`, `LogMatchAddGroundPanel.
 * jsx`, `LogMatchSavedStep.jsx`, `LogMatchScorecardStep.jsx`,
 * `ReflectionPrompt.jsx`, `RecordEmptyScreen.jsx`).
 */
export const ActionButton = React.forwardRef(function ActionButton(
  { icon = null, children, className = "", "data-testid": testId, style, disabled = false, type = "button", ...rest },
  ref
) {
  return (
    <button
      ref={ref}
      type={type}
      data-testid={testId}
      disabled={disabled}
      className={className}
      style={{
        display: "flex",
        width: "100%",
        alignItems: "center",
        justifyContent: "center",
        gap: icon ? 10 : 0,
        minHeight: "var(--nw-size-action)",
        padding: "0 20px",
        background: "var(--nw-gold)",
        color: "var(--nw-ink)",
        fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
        fontWeight: 700,
        fontSize: 16,
        border: "none",
        borderRadius: "var(--nw-radius-control)",
        boxShadow: "none",
        cursor: disabled ? "not-allowed" : "pointer",
        opacity: disabled ? 0.6 : 1,
        ...style,
      }}
      {...rest}
    >
      {icon && (
        <span
          aria-hidden="true"
          className="flex items-center justify-center"
          style={{ width: 20, height: 20, color: "var(--nw-ink)", flexShrink: 0 }}
        >
          {icon}
        </span>
      )}
      {children}
    </button>
  );
});

export default ActionButton;
